import cn from "@/v2-ui/utils/utils.cn"
import Tabs from "@/v2-ui/tabs"
import Button, { TooltipButton } from "@/v2-backoffice-client/components/button"
import type { FilterCombinator, FilterGroup } from "../table.types"
import TableAdvancedFilterCondition from "./TableAdvancedFilterCondition"
import {
  newCondition,
  newGroup,
  type AdvancedFilterColumn,
} from "./tableAdvancedFilter.utils"

export type TableAdvancedFilterGroupProps = {
  group: FilterGroup
  columns: AdvancedFilterColumn[]
  /** Depth in the tree; the root is 0. Used to cap nesting and indent. */
  depth: number
  onChange(next: FilterGroup): void
  /** Absent for the root group (which cannot remove itself). */
  onRemove?(): void
}

const MAX_DEPTH = 4

function TableAdvancedFilterGroup(props: TableAdvancedFilterGroupProps) {
  const { group, columns, depth, onChange, onRemove } = props

  const conditions = group.conditions ?? []
  const groups = group.groups ?? []

  function setCombinator(combinator: FilterCombinator) {
    onChange({ ...group, combinator })
  }

  function addCondition() {
    onChange({ ...group, conditions: [...conditions, newCondition(columns)] })
  }

  function addGroup() {
    onChange({ ...group, groups: [...groups, newGroup("OR")] })
  }

  return (
    <div
      className={cn(
        "ui-table-adv-filter-group",
        depth > 0 && "ui-table-adv-filter-group-nested",
      )}
    >
      {/* One constant sentence built from two segmented switches (the same
          Tabs component as the panel's Simple/Advanced switch):
          "[Include|Exclude] rows matching [all|any] of the conditions below".
          Fixed wording + fixed-width controls = no layout shift on toggle,
          and no competing "primary"-colored actions (Apply filters is the
          panel's only primary). */}
      <div className="ui-table-adv-filter-group-header">
        <Tabs
          value={group.negate ? "exclude" : "include"}
          onValueChange={(value) =>
            onChange({ ...group, negate: value === "exclude" })
          }
        >
          <Tabs.List>
            <Tabs.Trigger
              value="include"
              size="sm"
              title="Keep only the rows this group matches"
            >
              Include
            </Tabs.Trigger>
            <Tabs.Trigger
              value="exclude"
              size="sm"
              title="Hide the rows this group matches (NOT)"
            >
              Exclude
            </Tabs.Trigger>
          </Tabs.List>
        </Tabs>
        <span className="ui-table-adv-filter-combinator-label">
          rows matching
        </span>
        <Tabs
          value={group.combinator}
          onValueChange={(value) => setCombinator(value as FilterCombinator)}
        >
          <Tabs.List>
            <Tabs.Trigger
              value="AND"
              size="sm"
              title="All conditions must match (AND)"
            >
              all
            </Tabs.Trigger>
            <Tabs.Trigger
              value="OR"
              size="sm"
              title="Any one condition is enough (OR)"
            >
              any
            </Tabs.Trigger>
          </Tabs.List>
        </Tabs>
        <span className="ui-table-adv-filter-combinator-label">
          of the conditions below
        </span>

        <div className="ui-table-adv-filter-group-header-spacer" />

        {onRemove && (
          <TooltipButton
            variant="ghost"
            size="xs"
            isSquare
            iconSrc="trash"
            content="Remove group"
            onClick={onRemove}
          />
        )}
      </div>

      <div className="ui-table-adv-filter-group-body">
        {conditions.map((condition, index) => (
          <TableAdvancedFilterCondition
            // Index keys are acceptable here: rows aren't reordered, only
            // appended/removed, and each row is fully controlled.
            key={`c-${index}`}
            condition={condition}
            columns={columns}
            onChange={(next) => {
              const nextConditions = conditions.slice()
              nextConditions[index] = next
              onChange({ ...group, conditions: nextConditions })
            }}
            onRemove={() => {
              onChange({
                ...group,
                conditions: conditions.filter((_, i) => i !== index),
              })
            }}
          />
        ))}

        {groups.map((sub, index) => (
          <TableAdvancedFilterGroup
            key={`g-${index}`}
            group={sub}
            columns={columns}
            depth={depth + 1}
            onChange={(next) => {
              const nextGroups = groups.slice()
              nextGroups[index] = next
              onChange({ ...group, groups: nextGroups })
            }}
            onRemove={() => {
              onChange({
                ...group,
                groups: groups.filter((_, i) => i !== index),
              })
            }}
          />
        ))}
      </div>

      <div className="ui-table-adv-filter-group-actions">
        <Button
          variant="ghost"
          size="xs"
          iconSrc="plus"
          iconSize={12}
          onClick={addCondition}
          disabled={columns.length === 0}
        >
          Add Condition
        </Button>
        {depth < MAX_DEPTH && (
          <Button
            variant="ghost"
            size="xs"
            iconSrc="plus"
            iconSize={12}
            onClick={addGroup}
            disabled={columns.length === 0}
          >
            Add Group
          </Button>
        )}
      </div>
    </div>
  )
}

export default TableAdvancedFilterGroup
